import { useCallback, useRef, useState } from 'react';
import { openMicrophoneStream, MicStreamHandle } from '../lib/audio';

export interface UseMicrophoneResult {
  micOn: boolean;
  micError: string | null;
  /** Opens the mic and returns a stream of 16 kHz PCM16LE chunks. */
  startMic: () => Promise<MicStreamHandle>;
  /** Closes the current mic stream, if any. */
  stopMic: () => void;
}

const MIC_ERROR_MESSAGE =
  'Microphone access is required for the voice interview. Please allow microphone access and try again.';

export function useMicrophone(): UseMicrophoneResult {
  const [micOn, setMicOn] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const handleRef = useRef<MicStreamHandle | null>(null);

  const stopMic = useCallback(() => {
    handleRef.current?.stop();
    handleRef.current = null;
    setMicOn(false);
  }, []);

  const startMic = useCallback(async (): Promise<MicStreamHandle> => {
    setMicError(null);
    if (handleRef.current) return handleRef.current;
    try {
      const handle = await openMicrophoneStream();
      handleRef.current = handle;
      setMicOn(true);
      return handle;
    } catch (err) {
      const denied =
        typeof err === 'object' && err !== null && 'name' in err && (err as { name?: string }).name === 'NotAllowedError';
      setMicError(denied ? MIC_ERROR_MESSAGE : 'Could not access the microphone.' + (err instanceof Error ? ` ${err.message}` : ''));
      setMicOn(false);
      throw err;
    }
  }, []);

  return { micOn, micError, startMic, stopMic };
}